<template>
  <div class="template-shell">
    <div v-if="homePage" class="home-container">
      <component
        :is="dynamicComponent"
        :homeImage="homeImage"
        :exhibitIdProps="id"
        @exit="exitHomePage"
      />
    </div>

    <main v-else class="vertical-gallery">
      <aside class="intro-panel">
        <h1>{{ exhibitName }}</h1>
        <p>Ajoutez, explorez et faites défiler les contenus de l'exposition.</p>
      </aside>

      <section class="gallery-stage">
        <button class="scroll-button scroll-button--top" type="button" @click="scrollGallery(-1)" aria-label="Remonter">
          <q-icon name="keyboard_arrow_up" />
        </button>

        <div ref="galleryRef" class="media-grid" @scroll="updateScrollState">
          <div v-if="loading" class="state-panel">Chargement...</div>
          <div v-else-if="resources.length === 0" class="state-panel">Aucune ressource dans la galerie</div>

          <template v-else>
            <article
              v-for="(resource, index) in resources"
              :key="resource.id || index"
              class="media-tile"
              :style="{ '--delay': `${Math.min(index * 55, 520)}ms` }"
              @click="openLightbox(index)"
            >
              <img
                v-if="isImageResource(resource) && resourceUrls[index]"
                :src="resourceUrls[index]"
                :alt="resource.title || exhibitName"
                loading="lazy"
              />
              <video v-else-if="getResourceType(resource) === 'video' && resourceUrls[index]" :src="resourceUrls[index]" muted playsinline />
              <div v-else class="file-tile">
                <q-icon :name="fileIcon(resource)" />
                <span>{{ resourceUrls[index] === null ? 'Media indisponible' : resource.title || resource.name || 'Chargement...' }}</span>
              </div>
              <div class="tile-caption">
                <span>{{ resource.title || 'Ressource' }}</span>
              </div>
            </article>
          </template>
        </div>

        <button class="scroll-button scroll-button--bottom" type="button" @click="scrollGallery(1)" aria-label="Descendre">
          <q-icon name="keyboard_arrow_down" />
        </button>
      </section>
    </main>

    <transition name="fade">
      <div v-if="activeIndex !== null" class="lightbox" @click="closeLightbox">
        <div class="lightbox-content" @click.stop>
          <button class="close-button" type="button" @click="closeLightbox" aria-label="Fermer">
            <q-icon name="close" />
          </button>
          <button class="lightbox-nav lightbox-nav--prev" type="button" @click="navigateLightbox(-1)" aria-label="Précédent">
            <q-icon name="chevron_left" />
          </button>
          <img
            v-if="isImageResource(activeResource) && activeUrl"
            :src="activeUrl"
            :alt="activeResource.title || exhibitName"
          />
          <video v-else-if="getResourceType(activeResource) === 'video' && activeUrl" :src="activeUrl" controls autoplay playsinline />
          <div v-else class="file-tile file-tile--large">
            <q-icon :name="fileIcon(activeResource)" />
            <span>{{ activeUrl === null ? 'Media indisponible' : activeResource.title || activeResource.name || 'Chargement...' }}</span>
          </div>
          <button class="lightbox-nav lightbox-nav--next" type="button" @click="navigateLightbox(1)" aria-label="Suivant">
            <q-icon name="chevron_right" />
          </button>
          <div class="lightbox-caption">
            <strong>{{ activeResource.title || 'Ressource' }}</strong>
            <p>{{ activeResource.description }}</p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, defineProps, ref } from 'vue';
import { useGalleryTemplate } from '../../composables/useGalleryTemplate';

const props = defineProps({
  homePageProps: { type: Boolean, default: true },
  exhibitId: { type: String, default: null },
});

const {
  dynamicComponent,
  exhibitName,
  exitHomePage,
  getResourceType,
  homeImage,
  homePage,
  id,
  isImageResource,
  loading,
  resourceUrls,
  resources,
} = useGalleryTemplate(props);

const galleryRef = ref(null);
const activeIndex = ref(null);

const activeResource = computed(() => resources.value[activeIndex.value] || {});
const activeUrl = computed(() => resourceUrls.value[activeIndex.value] || '');

const scrollGallery = (direction) => {
  if (!galleryRef.value) return;
  const amount = Math.max(galleryRef.value.clientHeight * 0.72, 360);
  galleryRef.value.scrollBy({ top: direction * amount, behavior: 'smooth' });
};

const updateScrollState = () => {};

const openLightbox = (index) => {
  activeIndex.value = index;
};

const closeLightbox = () => {
  activeIndex.value = null;
};

const navigateLightbox = (direction) => {
  if (!resources.value.length || activeIndex.value === null) return;
  activeIndex.value = (activeIndex.value + direction + resources.value.length) % resources.value.length;
};

const fileIcon = (resource) => {
  const type = getResourceType(resource);
  if (type === 'audio') return 'audiotrack';
  if (type === 'pdf' || type === 'doc') return 'description';
  return 'insert_drive_file';
};
</script>

<style scoped>
.template-shell {
  min-height: 100vh;
  background: #000;
  color: #fff;
}

.vertical-gallery {
  display: grid;
  grid-template-columns: minmax(300px, 34vw) minmax(420px, 1fr);
  min-height: 100vh;
  overflow: hidden;
}

.intro-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding: clamp(34px, 5vw, 72px);
  animation: introIn 520ms ease both;
}

h1 {
  margin: 0 0 30px;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(46px, 5.2vw, 78px);
  font-weight: 400;
  line-height: 0.95;
  overflow-wrap: anywhere;
}

.intro-panel p:not(.eyebrow) {
  max-width: 340px;
  margin: 0;
  color: #f4f4f4;
  font-size: 28px;
  line-height: 1.35;
}

.gallery-stage {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  height: 100vh;
  padding: 96px 56px 96px 0;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 48px;
  width: 100%;
  height: auto;
  max-height: calc(100vh - 192px);
  overflow-y: auto;
  scroll-snap-type: y proximity;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding: 0;
}

.media-grid::-webkit-scrollbar {
  display: none;
}

.media-tile {
  position: relative;
  aspect-ratio: 1.22 / 1;
  overflow: hidden;
  background: #141414;
  cursor: pointer;
  scroll-snap-align: center;
  animation: tileIn 460ms ease both;
  animation-delay: var(--delay);
  transition: transform 180ms ease, filter 180ms ease;
}

.media-tile:hover {
  transform: translateY(-3px);
  filter: brightness(1.05);
}

.media-tile:active {
  transform: scale(0.985);
}

.media-tile img,
.media-tile video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 520ms ease;
}

.media-tile:hover img,
.media-tile:hover video {
  transform: scale(1.035);
}

.tile-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 36px 18px 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  color: #fff;
  font-size: 15px;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 160ms ease, transform 160ms ease;
}

.media-tile:hover .tile-caption {
  opacity: 1;
  transform: translateY(0);
}

.scroll-button,
.close-button,
.lightbox-nav {
  border: 0;
  cursor: pointer;
}

.scroll-button {
  position: absolute;
  left: 50%;
  z-index: 5;
  display: grid;
  place-items: center;
  width: 86px;
  height: 86px;
  border-radius: 50%;
  background: #fff;
  color: #000;
  font-size: 58px;
  transform: translateX(-50%);
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.scroll-button:hover {
  box-shadow: 0 14px 40px rgba(255, 255, 255, 0.18);
}

.scroll-button:active {
  transform: translateX(-50%) scale(0.94);
}

.scroll-button--top {
  top: 18px;
}

.scroll-button--bottom {
  bottom: 18px;
}

.file-tile {
  display: grid;
  place-items: center;
  gap: 14px;
  height: 100%;
  padding: 28px;
  text-align: center;
  background: #171717;
}

.file-tile .q-icon {
  font-size: 56px;
}

.state-panel {
  grid-column: 1 / -1;
  display: grid;
  place-items: center;
  min-height: 50vh;
  color: #d4d4d4;
}

.lightbox {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 22px;
  background: rgba(0, 0, 0, 0.9);
}

.lightbox-content {
  position: relative;
  display: grid;
  place-items: center;
  width: min(1180px, 94vw);
  height: min(820px, 88vh);
}

.lightbox-content img,
.lightbox-content video {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.close-button,
.lightbox-nav {
  position: absolute;
  z-index: 2;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #fff;
  color: #000;
}

.close-button {
  top: 10px;
  right: 10px;
  width: 44px;
  height: 44px;
}

.lightbox-nav {
  top: 50%;
  width: 58px;
  height: 58px;
  font-size: 34px;
  transform: translateY(-50%);
}

.lightbox-nav--prev {
  left: 10px;
}

.lightbox-nav--next {
  right: 10px;
}

.lightbox-caption {
  position: absolute;
  left: 18px;
  bottom: 18px;
  max-width: 520px;
  padding: 14px 16px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.64);
}

.lightbox-caption p {
  margin: 6px 0 0;
  color: #ddd;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 180ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes introIn {
  from {
    opacity: 0;
    transform: translateX(-16px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes tileIn {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1060px) {
  .vertical-gallery {
    grid-template-columns: 1fr;
    overflow: visible;
  }

  .intro-panel {
    padding: 42px 28px 28px;
  }

  h1 {
    font-size: 58px;
  }

  .intro-panel p:not(.eyebrow) {
    max-width: none;
    font-size: 21px;
  }

  .gallery-stage {
    height: auto;
    padding: 0 20px 28px;
  }

  .media-grid {
    height: auto;
    max-height: 72vh;
    gap: 18px;
  }
}

@media (max-width: 680px) {
  .media-grid {
    grid-template-columns: 1fr;
    max-height: none;
    overflow: visible;
    padding-bottom: 24px;
  }

  .scroll-button {
    display: none;
  }
}
</style>
